<template>
    <div class="modal-container">
        <!-- Darkened, blurred background overlay -->
        <div class="modal-background" @click="closeModal"></div>
  
        <!-- Modal Content -->
        <div class="modal-content">
          <h2>Questions? Concerns? Other Feedback?</h2>
          <p>We'd love to hear how we can improve HuntApply.com for you!</p>
  
          <!-- Error message -->
          <p v-if="error" class="error-message">{{ error }}</p>
          
          <!-- Success message -->
          <p v-if="successMessage" class="success-message">{{ successMessage }}</p>
  
          <!-- Text area for feedback -->
          <textarea 
            v-model="feedbackText" 
            placeholder="Write your feedback here..." 
            rows="6"
            :disabled="isLoading || hasSubmittedFeedback">
          </textarea>
  
          <!-- Action buttons -->
          <div class="modal-actions">
            <button 
              @click="submitFeedback" 
              :disabled="isLoading || hasSubmittedFeedback"
              class="submit-button"
              :class="{ loading: isLoading }">
              {{ isLoading ? 'Submitting...' : 'Submit' }}
            </button>
            <button 
              @click="closeModal" 
              :disabled="isLoading"
              class="cancel-button">
              Cancel
            </button>
          </div>
        </div>
    </div>
  </template>
  
  <script>
  export default {
    name: 'GiveFeedback',
    emits: ['close'],
    data() {
      return {
        feedbackText: '',
        isLoading: false,
        error: null,
        successMessage: null,
        hasSubmittedFeedback: false
      };
    },
    methods: {
      closeModal() {
        // Reset form state before closing
        this.feedbackText = '';
        this.error = null;
        this.successMessage = null;
        this.isLoading = false;
        this.hasSubmittedFeedback = false;
        this.$emit('close');
      },
      async submitFeedback() {
        if (!this.feedbackText.trim()) {
          this.error = 'Please enter your feedback';
          return;
        }

        this.isLoading = true;
        this.error = null;
        this.successMessage = null;

        try {
          const response = await fetch(`${this.$API_BASE_URL}/api/feedback`, {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              feedback: this.feedbackText
            })
          });

          if (response.ok) {
            this.successMessage = 'Thank you for your feedback to help us improve HuntApply.com!';
            this.hasSubmittedFeedback = true;
          } else {
            throw new Error('Failed to submit feedback');
          }
        } catch (error) {
          console.error('Error submitting feedback:', error);
          this.error = 'Failed to submit feedback. Please try again.';
        } finally {
          this.isLoading = false;
        }
      }
    }
  };
  </script>
  
  <style scoped>
  /* The container covers the entire screen when the modal is active */
  .modal-container {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 999; /* Ensure the modal appears on top */
  }
  
  /* Semi-transparent background overlay with blur effect */
  .modal-background {
    position: absolute;
    width: 100%;
    height: 100%;
    /* Dark semi-transparent overlay */
    background: rgba(0, 0, 0, 0.5);
    /* Blurs whatever is behind it */
    backdrop-filter: blur(5px);
  }
  
  /* The modal box itself */
  .modal-content {
    position: relative;
    /* Center the modal: push it down a bit from the top */
    margin: 10vh auto;
    max-width: 600px;
    background-color: #fff;
    border-radius: 8px;
    padding: 2rem;
    z-index: 1000; /* Above the overlay */
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
  }
  
  /* Style for the text area */
  textarea {
    width: 100%;
    padding: 0.8rem;
    font-size: 1rem;
    box-sizing: border-box;
    margin-bottom: 1rem;
    resize: vertical; /* Allow vertical resizing */
  }
  
  /* Message styles */
  .error-message {
    color: #dc3545;
    margin-bottom: 1rem;
    padding: 0.5rem;
    background-color: #f8d7da;
    border-radius: 4px;
  }

  .success-message {
    color: #28a745;
    margin-bottom: 1rem;
    padding: 0.5rem;
    background-color: #d4edda;
    border-radius: 4px;
  }

  /* Action buttons container */
  .modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
  }
  
  .modal-actions button {
    padding: 0.6rem 1.2rem;
    cursor: pointer;
    font-size: 1rem;
    border-radius: 4px;
    border: none;
    transition: all 0.2s ease;
  }

  .modal-actions button:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  .submit-button {
    background-color: #007bff;
    color: white;
  }

  .submit-button:hover:not(:disabled) {
    background-color: #0056b3;
  }

  .submit-button.loading {
    position: relative;
    background-color: #0056b3;
  }

  .cancel-button {
    background-color: #6c757d;
    color: white;
  }

  .cancel-button:hover:not(:disabled) {
    background-color: #5a6268;
  }
  </style>
